<script setup>
import { onClickOutside } from '@vueuse/core'
import { useNotificationApi } from '~/composables/api/useNotificationApi'
import NotificationDetailModal from '~/components/modals/NotificationDetailModal.vue'

const { t } = useI18n()
const { custom } = useCustomModal()

const isOpen = ref(false)
const containerRef = ref(null)

onClickOutside(containerRef, () => {
  isOpen.value = false
})

const { list, getUnreadedCount, markAsReaded, markAllAsReaded } = useNotificationApi()
const { data: notifications, refresh: refreshList } = await useAsyncData(
  'notifications-dropdown',
  () => list({ 'page[size]': 10 }),
  { server: false }
)

const { data: unreadCount, refresh: refreshCount } = await useAsyncData(
  'notifications-unread-count',
  () => getUnreadedCount(),
  { server: false, transform: (res) => res?.data?.count ?? 0 }
)

const unreadCountValue = computed(() => unreadCount.value ?? 0)

async function refreshAll() {
  await Promise.all([refreshList(), refreshCount()])
}

function toggleOpen() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    refreshAll()
  }
}

async function handleNotificationClick(notification) {
  isOpen.value = false
  await custom(NotificationDetailModal, {
    notification
  }, {
    closeOnOverlay: true,
    closeOnEscape: true
  })
  await markAsReaded(notification.id)
  await refreshAll()
}

async function handleMarkAllAsReaded() {
  await markAllAsReaded()
  await refreshAll()
}

function handleViewAll() {
  isOpen.value = false
  navigateTo('/notifications')
}
</script>

<template>
  <div ref="containerRef" class="relative">
    <button
      class="flex items-center justify-center rounded-lg size-10 bg-slate-100 text-slate-600 hover:text-primary hover:bg-primary/10 transition-all relative"
      :class="{ 'bg-primary/10 text-primary': isOpen }"
      :aria-label="t('notifications.title')"
      @click.stop="toggleOpen"
    >
      <Icon name="material-symbols:notifications" class="text-[20px]" />
      <span
        v-if="unreadCountValue > 0"
        class="absolute top-2 right-2 size-2 bg-red-500 rounded-full border-2 border-white"
      />
    </button>

    <div
      v-show="isOpen"
      class="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50"
    >
      <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div class="flex items-start gap-1">
          <h3 class="font-semibold text-slate-800">
            {{ t('notifications.title') }}
          </h3>
          <span
            v-if="unreadCountValue > 0"
            class="min-w-4 h-4 px-1 -mt-1 flex items-center justify-center text-[10px] font-semibold bg-red-500 text-white rounded-full"
          >
            {{ unreadCountValue }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-if="unreadCountValue > 0"
            class="text-xs text-slate-500 hover:text-primary transition-colors whitespace-nowrap"
            @click="handleMarkAllAsReaded"
          >
            {{ t('notifications.markAllRead') }}
          </button>
        </div>
      </div>

      <div class="max-h-96 overflow-y-auto">
        <NotificationListItem
          v-for="notification in notifications?.data || []"
          :key="notification.id"
          :notification="notification"
          @click="handleNotificationClick"
        />

        <div
          v-if="!notifications?.data?.length"
          class="flex flex-col items-center justify-center py-8 text-slate-400"
        >
          <Icon name="material-symbols:notifications-off" class="text-4xl mb-2" />
          <p class="text-sm">{{ t('notifications.empty') }}</p>
        </div>
      </div>

      <div
        v-if="notifications?.data?.length"
        class="px-4 py-2 border-t border-slate-100 bg-slate-50"
      >
        <button
          class="w-full text-center text-sm font-medium text-slate-600 hover:text-primary transition-colors"
          @click="handleViewAll"
        >
          {{ t('notifications.viewAll') }}
        </button>
      </div>
    </div>
  </div>
</template>
